import type { Me } from '@baby-tracker/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';

import { useMe } from './useMe.js';

const STORAGE_KEY = 'activeFamilyId';

function readStoredFamilyId(): string | null {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

/** The family the UI is currently showing. The id is not a secret; access is still checked by the API. */
export function useActiveFamily(): {
    me: UseQueryResult<Me>;
    families: Me['families'];
    active: Me['families'][number] | undefined;
    setActiveFamilyId: (familyId: string) => void;
} {
    const me = useMe();
    const families = me.data?.families ?? [];
    const [selectedId, setSelectedId] = useState<string | null>(readStoredFamilyId);
    const active = families.find((family) => family.id === selectedId) ?? families[0];

    function setActiveFamilyId(familyId: string): void {
        try {
            localStorage.setItem(STORAGE_KEY, familyId);
        } catch {
            // Storage can be unavailable in private mode. The selection still applies for this visit.
        }
        setSelectedId(familyId);
    }

    return { me, families, active, setActiveFamilyId };
}
